import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FieldArrayType, FormlyField, FieldArrayTypeConfig } from '@ngx-formly/core';

import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MAX_SHM_CHANNELS } from '@cosmos/shared';

@Component({
  selector: 'cosmos-shm-data-channels',
  imports: [ButtonModule, MessageModule, TooltipModule, FormlyField, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-3 mb-3">

      <div class="flex justify-between items-center">
        <span class="text-sm font-semibold text-surface-600 dark:text-surface-400">
          <i class="fa-solid fa-microchip mr-2"></i>{{ props.label | translate }} ({{ model?.length || 0 }})
        </span>
        <p-button
          icon="fa-solid fa-plus"
          [label]="'button.add' | translate"
          [outlined]="true"
          size="small"
          (onClick)="addChannel()"
          [disabled]="(model?.length || 0) >= maxItems"
        />
      </div>

      @if (!model || model?.length === 0) {
        <p-message severity="info" styleClass="w-full">
          <i class="fa-solid fa-microchip mr-2"></i>{{ 'measurement.shm.noChannels' | translate }}
        </p-message>
      } @else {
        <div class="flex flex-col gap-3">
          @for (itemField of field.fieldGroup; let i = $index; track itemField) {
            <div class="border border-surface-200 dark:border-surface-700 rounded-border p-4">
              <div class="flex justify-between items-start gap-3">

                <span class="text-xs font-semibold text-surface-500 dark:text-surface-400 shrink-0 mt-1">
                  #{{ i + 1 }}
                </span>

                <div class="flex-1 min-w-0">
                  <formly-field [field]="itemField"></formly-field>
                </div>

                <div class="shrink-0">
                  @if ((model?.length || 0) > 1) {
                    <p-button
                      icon="fa-solid fa-trash"
                      [text]="true"
                      severity="danger"
                      (onClick)="remove(i)"
                      [pTooltip]="'button.delete' | translate"
                      tooltipPosition="top"
                    />
                  }
                </div>

              </div>
            </div>
          }
        </div>
      }

    </div>
  `,
})
export class SHMDataChannelsComponent extends FieldArrayType<FieldArrayTypeConfig> {
  private readonly translateService = inject(TranslateService);

  readonly maxItems = MAX_SHM_CHANNELS;

  addChannel() {
    if (!this.model || !Array.isArray(this.model)) {
      this.formControl.setValue([]);
    }

    const currentModel = this.model || [];
    if (currentModel.length < this.maxItems) {
      const usedChannels = currentModel.map((m: any) => m.channelId);
      let nextAvailable = 1;
      for (let i = 1; i <= this.maxItems; i++) {
        if (!usedChannels.includes(i)) {
          nextAvailable = i;
          break;
        }
      }

      this.add(currentModel.length, {
        channelId: nextAvailable,
        type: 'None',
        parameters: {
          sensitivity: 1,
          offset: 0
        }
      });
    }
  }
}
